/** @format */

// Format date to readable string
export const formatDate = (date: Date | string | number): string => {
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;

  if (isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  const now = new Date();
  const diffTime = Math.abs(now.getTime() - dateObj.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Today";
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? "s" : ""} ago`;
  } else {
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
};

// Format duration in minutes to readable string
export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
};

// Format file size in bytes to readable string
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Truncate text to specified length
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

// Format currency (Ghanaian Cedi)
export const formatCurrency = (amount: number | undefined | null): string => {
  // Handle undefined, null, or NaN values
  if (amount === undefined || amount === null || isNaN(amount)) {
    return 'GH₵0.00';
  }
  
  return new Intl.NumberFormat("en-GH", {
    style: "currency",
    currency: "GHS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Formats a number as Ghana Cedis currency
 * @param amount - The amount to format
 * @param showSymbol - Whether to show the GH₵ symbol (default: true)
 * @returns Formatted currency string
 */
export const formatGhanaCedis = (amount: number | undefined | null, showSymbol: boolean = true): string => {
  // Handle undefined, null, or NaN values
  if (amount === undefined || amount === null || isNaN(amount)) {
    return showSymbol ? 'GH₵0.00' : '0.00';
  }
  
  const formatter = new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  
  const formatted = formatter.format(amount);
  
  // If we don't want the symbol, remove it
  if (!showSymbol) {
    return formatted.replace('GH₵', '').trim();
  }
  
  return formatted;
};

/**
 * Formats a number as Ghana Cedis without the currency symbol
 * @param amount - The amount to format
 * @returns Formatted amount string
 */
export const formatAmount = (amount: number | undefined | null): string => {
  return formatGhanaCedis(amount, false);
};

// Affiliate link generation utilities
export const generateAffiliateLink = (baseUrl: string, referralCode?: string | null): string => {
  if (!referralCode) return baseUrl;
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}ref=${referralCode}`;
};

export const generateCourseAffiliateLink = (courseId: string, referralCode?: string | null): string => {
  // Use public course route instead of protected app route
  const baseUrl = `${window.location.origin}/courses/${courseId}`;
  return generateAffiliateLink(baseUrl, referralCode);
};

export const generateBookAffiliateLink = (bookId: string, referralCode?: string | null): string => {
  // Use public shop route instead of protected app route
  const baseUrl = `${window.location.origin}/shop?book=${bookId}`;
  return generateAffiliateLink(baseUrl, referralCode);
};

export const generateAffiliateText = (title: string, referralCode?: string | null): string => {
  if (!referralCode) {
    return `Check out this amazing content: ${title}`;
  }
  return `Check out this amazing content: ${title}\n\nUse my referral code "${referralCode}" for special benefits!`;
};

// Get initials from name
export const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

// Format progress percentage with color classes
export const getProgressColor = (progress: number): string => {
  if (progress >= 80) return "bg-green-500";
  if (progress >= 60) return "bg-blue-500";
  if (progress >= 40) return "bg-yellow-500";
  if (progress >= 20) return "bg-orange-500";
  return "bg-gray-500";
};

// Format course status
export const getCourseStatusInfo = (progress: number) => {
  if (progress === 100) {
    return { text: "Completed", color: "bg-green-100 text-green-800" };
  } else if (progress > 0) {
    return { text: "In Progress", color: "bg-blue-100 text-blue-800" };
  } else {
    return { text: "Not Started", color: "bg-gray-100 text-gray-800" };
  }
};

/**
 * Creates a complete user profile object with all required fields
 * @param uid - User ID
 * @param userData - Basic user data
 * @returns Complete user profile object
 */
export const createCompleteUserProfile = (
  uid: string,
  userData: {
    email: string;
    name: string;
    telephone?: string;
    role?: "student" | "author" | "admin" | "pending";
    program?: "ICAG" | "CITG" | null;
    registern?: string;
    referralCode?: string | null;
  }
) => {
  const now = new Date();

  return {
    uid,
    email: userData.email,
    name: userData.name,
    telephone: userData.telephone || null,
    role: userData.role === "author" ? "pending" : (userData.role || "student"),
    status: userData.role === "author" ? "pending" : "Offline",
    program: userData.program || null,
    createdAt: now,
    updatedAt: now,
    lastLogin: now,
    profileComplete: false,
    referralCode: userData.referralCode || null,
    referredBy: null,
    affiliateLink: null,
    earnings: 0,
    totalrefs: 0,
    registern: userData.registern || "",
    underef: null,
    message_id: null,
    state: "ACTIVE",
  };
};

/**
 * Formats user role for display purposes
 * @param role - User's actual role
 * @returns Formatted role string for display
 */
export const formatUserRole = (role?: string): string => {
  if (!role || role === 'undefined' || role === 'null') return 'Student';
  
  switch (role) {
    case 'pending':
      return 'Guest';
    case 'student':
      return 'Student';
    case 'author':
      return 'Author';
    case 'admin':
      return 'Administrator';
    default:
      return 'Student'; // Changed from 'Guest' to 'Student' for any unknown role
  }
};

/**
 * Gets user role display text with proper formatting
 * @param role - User's actual role
 * @returns Formatted role string for display
 */
export const getUserRoleDisplay = (role?: string): string => {
  return formatUserRole(role);
};

/**
 * Converts text with URLs into HTML string with clickable links
 * @param text - The text content that may contain URLs
 * @returns HTML string with clickable links
 */
export const renderTextWithLinks = (text: string): string => {
  if (!text) return '';
  
  let formattedText = text;

  // 1. Markdown: Bold (**text** or __text__)
  formattedText = formattedText.replace(/(\*\*|__)(.*?)\1/g, '<strong>$2</strong>');

  // 2. Markdown: Italic (*text* or _text_)
  formattedText = formattedText.replace(/(\*|_)(.*?)\1/g, '<em>$2</em>');

  // 3. Markdown: Inline code (`code`)
  formattedText = formattedText.replace(/`([^`]+)`/g, '<code class="bg-indigo-50 px-1.5 py-0.5 rounded text-indigo-700 font-mono text-[0.8em] font-medium">$1</code>');

  // 4. Markdown: Blockquote (> text)
  formattedText = formattedText.replace(/^>\s*(.+)$/gm, '<blockquote class="border-l-4 border-indigo-200 pl-3 italic text-slate-600 my-2">$1</blockquote>');

  // 5. Markdown: Lists (Bulleted: * or - or +)
  formattedText = formattedText.replace(/^\s*[\*\-\+]\s*(.+)$/gm, '<li class="ml-4 mb-1 list-disc text-inherit">$1</li>');
  // Wrap li in ul if found
  if (formattedText.includes('<li')) {
    // Simple wrap logic - better to have a real parser but this is a quick improvement
    // We'll wrap segments of list items
  }

  // 6. URL regex pattern to detect http/https links
  const urlRegex = /(https?:\/\/[^\s]+)/gi;
  
  // Replace URLs with HTML anchor tags (optimized for the new UI)
  return formattedText.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-indigo-600 hover:text-indigo-800 underline break-all font-medium">${url}</a>`;
  });
};
